/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

/**
 * A growable byte sink. Multi-byte writes default to little-endian, which is what
 * every FIT producer uses, but take the endianness the caller's message
 * definition announced.
 *
 * [replaceRange] exists for one reason: a FIT file header states the size of the
 * data that follows it, which is only known once encoding is done, so the
 * encoder reserves the header up front and patches it in [FitEncoder.close].
 */
public class ByteWriter(initialCapacity: Int = 4096) {
    private var buffer = ByteArray(if (initialCapacity > 0) initialCapacity else 16)

    public var size: Int = 0
        private set

    public val isEmpty: Boolean get() = size == 0

    private fun ensureCapacity(additional: Int) {
        val required = size + additional
        if (required <= buffer.size) return
        var newSize = buffer.size
        while (newSize < required) newSize *= 2
        buffer = buffer.copyOf(newSize)
    }

    public fun writeByte(value: UByte) {
        ensureCapacity(1)
        buffer[size++] = value.toByte()
    }

    public fun writeBytes(bytes: ByteArray) {
        ensureCapacity(bytes.size)
        bytes.copyInto(buffer, size)
        size += bytes.size
    }

    private fun writeUnsigned(value: ULong, byteCount: Int, endianness: Endianness) {
        ensureCapacity(byteCount)
        if (endianness == Endianness.LITTLE) {
            for (i in 0 until byteCount) {
                buffer[size + i] = ((value shr (8 * i)) and 0xFFuL).toByte()
            }
        } else {
            for (i in 0 until byteCount) {
                buffer[size + i] = ((value shr (8 * (byteCount - 1 - i))) and 0xFFuL).toByte()
            }
        }
        size += byteCount
    }

    public fun writeUShort(value: UShort, endianness: Endianness = Endianness.LITTLE): Unit =
        writeUnsigned(value.toULong(), 2, endianness)

    public fun writeShort(value: Short, endianness: Endianness = Endianness.LITTLE): Unit =
        writeUnsigned(value.toUShort().toULong(), 2, endianness)

    public fun writeUInt(value: UInt, endianness: Endianness = Endianness.LITTLE): Unit =
        writeUnsigned(value.toULong(), 4, endianness)

    public fun writeInt(value: Int, endianness: Endianness = Endianness.LITTLE): Unit =
        writeUnsigned(value.toUInt().toULong(), 4, endianness)

    public fun writeULong(value: ULong, endianness: Endianness = Endianness.LITTLE): Unit =
        writeUnsigned(value, 8, endianness)

    public fun writeLong(value: Long, endianness: Endianness = Endianness.LITTLE): Unit =
        writeUnsigned(value.toULong(), 8, endianness)

    public fun writeFloat(value: Float, endianness: Endianness = Endianness.LITTLE): Unit =
        writeUnsigned(value.toRawBits().toUInt().toULong(), 4, endianness)

    public fun writeDouble(value: Double, endianness: Endianness = Endianness.LITTLE): Unit =
        writeUnsigned(value.toRawBits().toULong(), 8, endianness)

    /** Writes the UTF-8 bytes of [value] followed by a NUL terminator. */
    public fun writeString(value: String) {
        writeBytes(value.encodeToByteArray())
        writeByte(0u)
    }

    /** Overwrites [bytes.size] bytes starting at [startIndex], which must already be written. */
    public fun replaceRange(startIndex: Int, bytes: ByteArray) {
        require(startIndex >= 0 && startIndex + bytes.size <= size) {
            "replaceRange($startIndex, ${bytes.size}) is outside the $size bytes written"
        }
        bytes.copyInto(buffer, startIndex)
    }

    /**
     * CRC of the bytes written so far, computed in place.
     *
     * Exists so [FitEncoder.close] can checksum the file without first copying
     * it out — on a large activity the copy costs more than the CRC.
     */
    internal fun crc(): UShort = Crc.calculate(buffer, 0, size)

    public fun toByteArray(): ByteArray = buffer.copyOf(size)
}
